import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  Users, 
  Calendar, 
  MessageCircle, 
  Bot, 
  TrendingUp, 
  LogOut,
  Mail,
  Phone,
  MapPin,
  Clock,
  CheckCircle,
  AlertCircle,
  Search,
  Filter,
  Download,
  Bell,
  Settings,
  BarChart3,
  PieChart,
  Activity,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  ArrowRight,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Eye,
  Trash2,
  Edit,
  Plus,
  X,
  CheckSquare,
  Square,
  FileText,
  Home,
  Briefcase,
  Sparkles,
  Target,
  Zap,
  User,
  Check,
  BookOpen
} from 'lucide-react';
import { toast } from 'sonner';
import { BlogTab } from './BlogTab';
import { InvoiceTab } from './InvoiceTab';
import { Textarea } from '../ui/textarea';

const EASTERN_TIME_ZONE = 'America/New_York';

const hasExplicitTimezone = (dateString) =>
  typeof dateString === 'string' && /(Z|[+-]\d{2}:?\d{2})$/i.test(dateString.trim());

const getEasternOffsetMinutes = (date) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: EASTERN_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
  }).formatToParts(date);

  const values = Object.fromEntries(
    parts.filter(part => part.type !== 'literal').map(part => [part.type, Number(part.value)])
  );
  const easternAsUtc = Date.UTC(
    values.year,
    values.month - 1,
    values.day,
    values.hour,
    values.minute,
    values.second
  );

  return (easternAsUtc - date.getTime()) / 60000;
};

const easternWallTimeToDate = ({ year, month, day, hour = 0, minute = 0, second = 0 }) => {
  const utcGuess = Date.UTC(year, month - 1, day, hour, minute, second);
  const firstPass = new Date(utcGuess - getEasternOffsetMinutes(new Date(utcGuess)) * 60000);
  const correctedOffset = getEasternOffsetMinutes(firstPass);
  return new Date(utcGuess - correctedOffset * 60000);
};

const parseAdminDate = (dateString, { timezoneLessIsUtc = false } = {}) => {
  if (!dateString) return null;
  if (dateString instanceof Date) return dateString;
  if (hasExplicitTimezone(dateString)) return new Date(dateString);

  const match = String(dateString).trim().match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?)?/
  );

  if (match) {
    if (timezoneLessIsUtc) {
      return new Date(Date.UTC(
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3]),
        Number(match[4] || 0),
        Number(match[5] || 0),
        Number(match[6] || 0)
      ));
    }

    return easternWallTimeToDate({
      year: Number(match[1]),
      month: Number(match[2]),
      day: Number(match[3]),
      hour: Number(match[4] || 0),
      minute: Number(match[5] || 0),
      second: Number(match[6] || 0)
    });
  }

  return new Date(dateString);
};

const getEasternParts = (dateInput, options = {}) => {
  const date = dateInput instanceof Date ? dateInput : parseAdminDate(dateInput, options);
  if (!date || Number.isNaN(date.getTime())) return null;

  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: EASTERN_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(date);
  const values = Object.fromEntries(
    parts.filter(part => part.type !== 'literal').map(part => [part.type, Number(part.value)])
  );

  return {
    year: values.year,
    month: values.month,
    day: values.day,
    monthKey: `${values.year}-${String(values.month).padStart(2, '0')}`
  };
};

const getCurrentEasternParts = () => getEasternParts(new Date());

const formatEasternMonth = (monthKey) => {
  const [year, month] = monthKey.split('-').map(Number);
  return easternWallTimeToDate({ year, month, day: 1 }).toLocaleDateString('en-US', {
    timeZone: EASTERN_TIME_ZONE,
    month: 'long',
    year: 'numeric'
  });
};

const isSameEasternDay = (dateInput, easternParts, options = {}) => {
  const dateParts = getEasternParts(dateInput, options);
  return dateParts?.year === easternParts.year &&
    dateParts?.month === easternParts.month &&
    dateParts?.day === easternParts.day;
};

export const AdminDashboard = ({ token, onLogout }) => {
  // State management
  const [stats, setStats] = useState({});
  const [contacts, setContacts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [chatbotQuotes, setChatbotQuotes] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterService, setFilterService] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [dismissedNotifications, setDismissedNotifications] = useState(new Set());
  const [selectedItems, setSelectedItems] = useState([]);
  const [calendarDate, setCalendarDate] = useState(new Date());
  // Revenue month filter
  const [revenueMonth, setRevenueMonth] = useState('current');
  const [revenueMonthData, setRevenueMonthData] = useState(null);
  const [invoiceRefreshKey, setInvoiceRefreshKey] = useState(0);
  // Real data from backend
  const [tasks, setTasks] = useState([]);
  const [payments, setPayments] = useState([]);
  const [revenueSummary, setRevenueSummary] = useState({
    grossRevenue: 0,
    netRevenue: 0,
    monthlyGrossRevenue: 0,
    monthlyNetRevenue: 0,
    monthlyContractorCosts: 0,
    totalContractorCosts: 0,
    lastMonthRevenue: 0,
    growthPercentage: 0,
    outstandingBalance: 0,
    totalLaborHours: 0,
    monthlyLaborHours: 0,
    breakdown: { deposits: 0, partialPayments: 0, fullPayments: 0, refunds: 0, contractorCosts: 0 }
  });
  // Modal states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPaymentHistoryModal, setShowPaymentHistoryModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showCostModal, setShowCostModal] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetConfirmation, setResetConfirmation] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [dbStats, setDbStats] = useState({ contacts: 0, bookings: 0, payments: 0, tasks: 0 });
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [editingPayment, setEditingPayment] = useState(null);
  const [correctionTarget, setCorrectionTarget] = useState('totalCost');
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    paymentType: 'deposit',
    paymentMethod: 'cash',
    transactionId: '',
    paymentDate: new Date().toISOString().split('T')[0],
    lineItems: [{ description: '', quantity: '1', unitPrice: '' }],
    notes: ''
  });
  const [taskForm, setTaskForm] = useState({ title: '', description: '', taskType: 'follow_up', priority: 'medium', dueDate: '' });
  const [costForm, setCostForm] = useState({ totalCost: '', contractorCost: '', laborHours: '' });
  const [convertForm, setConvertForm] = useState({ serviceType: '', preferredDate: '', currentAddress: '', newAddress: '' });

  const apiHeaders = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  const resetPaymentForm = (booking = null) => {
    setEditingPayment(null);
    setPaymentForm({
      amount: booking?.totalCost ? String(booking.totalCost) : '',
      paymentType: booking?.status === 'completed' ? 'full' : 'deposit',
      paymentMethod: 'cash',
      transactionId: '',
      paymentDate: new Date().toISOString().split('T')[0],
      lineItems: buildDefaultLineItems(booking),
      notes: ''
    });
  };

  useEffect(() => {
    fetchDashboardData();
    // Generate notifications from pending items
    generateNotifications();
  }, []);

  const generateNotifications = () => {
    const newNotifications = [];
    if (stats.pending_contacts > 0) {
      newNotifications.push({
        id: 1,
        type: 'warning',
        message: `${stats.pending_contacts} new contact(s) awaiting response`,
        time: 'Just now'
      });
    }
    setNotifications(newNotifications);
  };

  const fetchDashboardData = async () => {
    setIsLoading(true);
    try {
      const [statsRes, contactsRes, bookingsRes, quotesRes, tasksRes, paymentsRes, revenueRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/dashboard/stats`, { headers: apiHeaders }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/contacts`, { headers: apiHeaders }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/bookings`, { headers: apiHeaders }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/chatbot-quotes`, { headers: apiHeaders }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/tasks`, { headers: apiHeaders }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/payments`, { headers: apiHeaders }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/revenue/summary`, { headers: apiHeaders })
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (contactsRes.ok) setContacts(await contactsRes.json());
      if (bookingsRes.ok) setBookings(await bookingsRes.json());
      if (quotesRes.ok) setChatbotQuotes(await quotesRes.json());
      if (tasksRes.ok) setTasks(await tasksRes.json());
      if (paymentsRes.ok) setPayments(await paymentsRes.json());
      if (revenueRes.ok) setRevenueSummary(await revenueRes.json());

      toast.success('Dashboard data refreshed');
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Error loading dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  // Filter functions
  const filteredContacts = useMemo(() => {
    return contacts.filter(contact => {
      const matchesSearch = contact.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           contact.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           contact.message?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = filterStatus === 'all' || contact.status === filterStatus;
      return matchesSearch && matchesStatus;
    });
  }, [contacts, searchQuery, filterStatus]);

  const filteredBookings = useMemo(() => {
    return bookings.filter(booking => {
      const matchesSearch = booking.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           booking.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           booking.serviceType?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = filterStatus === 'all' || booking.status === filterStatus;
      const matchesService = filterService === 'all' || booking.serviceType?.toLowerCase().includes(filterService.toLowerCase());
      return matchesSearch && matchesStatus && matchesService;
    });
  }, [bookings, searchQuery, filterStatus, filterService]);

  const filteredChatbotQuotes = useMemo(() => {
    return chatbotQuotes.filter(quote => {
      const matchesSearch = quote.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           quote.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           quote.message?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch;
    });
  }, [chatbotQuotes, searchQuery]);

  // Export function
  const exportData = (data, filename) => {
    const csvContent = convertToCSV(data);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success(`${filename} exported successfully`);
  };

  const convertToCSV = (data) => {
    if (data.length === 0) return '';
    const headers = Object.keys(data[0]).filter(key => key !== '_id');
    const csvRows = [headers.join(',')];
    data.forEach(row => {
      const values = headers.map(header => {
        const val = row[header];
        return typeof val === 'string' ? `"${val.replace(/"/g, '""')}"` : val;
      });
      csvRows.push(values.join(','));
    });
    return csvRows.join('\n');
  };

  const updateContactStatus = async (contactId, newStatus) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/contacts/${contactId}/status`, {
        method: 'PUT',
        headers: apiHeaders,
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        toast.success(`Contact marked as ${newStatus}`);
        fetchDashboardData();
      }
    } catch (error) {
      toast.error('Error updating contact status');
    }
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/bookings/${bookingId}/status`, {
        method: 'PUT',
        headers: apiHeaders,
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        toast.success(`Booking marked as ${newStatus}`);
        fetchDashboardData();
      }
    } catch (error) {
      toast.error('Error updating booking status');
    }
  };

  // Task Management Functions
  const toggleTask = async (taskId) => {
    try {
      const task = tasks.find(t => t.id === taskId);
      const newStatus = task?.status === 'completed' ? 'pending' : 'completed';
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/tasks/${taskId}`, {
        method: 'PUT',
        headers: apiHeaders,
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        toast.success(`Task marked as ${newStatus}`);
        fetchDashboardData();
      }
    } catch (error) {
      toast.error('Error updating task');
    }
  };

  const createTask = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/tasks`, {
        method: 'POST',
        headers: apiHeaders,
        body: JSON.stringify({
          bookingId: selectedBooking?.id || null,
          title: taskForm.title,
          description: taskForm.description,
          taskType: taskForm.taskType,
          priority: taskForm.priority,
          dueDate: taskForm.dueDate ? parseAdminDate(taskForm.dueDate).toISOString() : null
        })
      });
      if (response.ok) {
        toast.success('Task created successfully');
        setShowTaskModal(false);
        setTaskForm({ title: '', description: '', taskType: 'follow_up', priority: 'medium', dueDate: '' });
        fetchDashboardData();
      }
    } catch (error) {
      toast.error('Error creating task');
    }
  };

  const deleteTask = async (taskId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/tasks/${taskId}`, {
        method: 'DELETE',
        headers: apiHeaders
      });
      if (response.ok) {
        toast.success('Task deleted');
        fetchDashboardData();
      }
    } catch (error) {
      toast.error('Error deleting task');
    }
  };

  // Payment Functions
  const buildDefaultLineItems = (booking) => {
    const totalCost = booking?.totalCost ? String(booking.totalCost) : '';
    return [{
      description: booking?.serviceType || 'Completed service',
      quantity: '1',
      unitPrice: totalCost
    }];
  };

  const openPaymentModalForBooking = (booking) => {
    setSelectedBooking(booking);
    resetPaymentForm(booking);
    setShowPaymentModal(true);
  };

  const openPaymentModalForEdit = (payment, bookingOverride = null) => {
    const linkedBooking = bookingOverride || bookings.find(b => b.id === payment.bookingId) || null;
    setSelectedBooking(linkedBooking);
    setEditingPayment(payment);
    setPaymentForm({
      amount: String(payment.amount ?? ''),
      paymentType: payment.paymentType || 'deposit',
      paymentMethod: payment.paymentMethod || 'cash',
      transactionId: payment.transactionId || '',
      paymentDate: payment.paymentDate
        ? new Date(payment.paymentDate).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0],
      lineItems: (payment.lineItems?.length ? payment.lineItems : buildDefaultLineItems(linkedBooking)).map((item) => ({
        description: item.description || '',
        quantity: String(item.quantity ?? 1),
        unitPrice: String(item.unitPrice ?? '')
      })),
      notes: payment.notes || ''
    });
    setShowPaymentHistoryModal(false);
    setShowPaymentModal(true);
  };

  const updatePaymentLineItem = (index, field, value) => {
    setPaymentForm(prev => ({
      ...prev,
      lineItems: prev.lineItems.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const addPaymentLineItem = () => {
    setPaymentForm(prev => ({
      ...prev,
      lineItems: [...prev.lineItems, { description: '', quantity: '1', unitPrice: '' }]
    }));
  };

  const removePaymentLineItem = (index) => {
    setPaymentForm(prev => ({
      ...prev,
      lineItems: prev.lineItems.length === 1
        ? prev.lineItems
        : prev.lineItems.filter((_, itemIndex) => itemIndex !== index)
    }));
  };

  const savePayment = async () => {
    if (!selectedBooking || !paymentForm.amount) {
      toast.error('Please select a booking and enter an amount');
      return;
    }
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/payments${editingPayment ? `/${editingPayment.id}` : ''}`,
        {
          method: editingPayment ? 'PUT' : 'POST',
          headers: apiHeaders,
          body: JSON.stringify({
            ...(editingPayment ? {} : { bookingId: selectedBooking.id }),
            amount: parseFloat(paymentForm.amount),
            paymentType: paymentForm.paymentType,
            paymentMethod: paymentForm.paymentMethod,
            transactionId: paymentForm.transactionId || null,
            paymentDate: paymentForm.paymentDate || null,
            lineItems: paymentForm.lineItems
              .filter(item => item.description.trim())
              .map(item => ({
                description: item.description.trim(),
                quantity: parseFloat(item.quantity || 1),
                unitPrice: parseFloat(item.unitPrice || 0)
              })),
            notes: paymentForm.notes
          })
        }
      );
      if (response.ok) {
        const data = await response.json();
        toast.success(
          editingPayment
            ? (
              data.invoiceGenerated && data.invoice?.invoiceNumber
                ? `Payment updated and invoice ${data.invoice.invoiceNumber} refreshed`
                : 'Payment updated successfully'
            )
            : data.invoiceGenerated && data.invoice?.invoiceNumber
            ? `Payment logged and invoice ${data.invoice.invoiceNumber} created`
            : 'Payment logged successfully'
        );
        setShowPaymentModal(false);
        resetPaymentForm();
        setInvoiceRefreshKey(prev => prev + 1);
        fetchDashboardData();
      } else {
        const error = await response.json();
        toast.error(error.detail || (editingPayment ? 'Error updating payment' : 'Error logging payment'));
      }
    } catch (error) {
      toast.error(editingPayment ? 'Error updating payment' : 'Error logging payment');
    }
  };

  const deletePayment = async (paymentId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/payments/${paymentId}`, {
        method: 'DELETE',
        headers: apiHeaders
      });
      if (response.ok) {
        const data = await response.json();
        if (data.invoiceGenerated && data.invoice?.invoiceNumber) {
          toast.success(`Payment deleted and invoice ${data.invoice.invoiceNumber} refreshed`);
        } else {
        toast.success('Payment deleted');
        }
        setInvoiceRefreshKey(prev => prev + 1);
        fetchDashboardData();
      }
    } catch (error) {
      toast.error('Error deleting payment');
    }
  };

  const bookingHasInvoice = (bookingId) => payments.some(
    payment => payment.bookingId === bookingId && payment.invoiceId
  );

  const getBookingPayments = (bookingId) => (
    payments
      .filter(payment => payment.bookingId === bookingId)
      .sort((a, b) => new Date(b.paymentDate || b.createdAt) - new Date(a.paymentDate || a.createdAt))
  );

  const openPaymentHistoryModal = (booking) => {
    setSelectedBooking(booking);
    setCorrectionTarget('paid');
    setShowPaymentHistoryModal(true);
  };

  const openPaymentCorrectionModal = (booking, target = 'paid') => {
    setSelectedBooking(booking);
    setCorrectionTarget(target);
    setShowPaymentHistoryModal(true);
  };

  const openFinancialsModal = (booking, target = 'totalCost') => {
    setSelectedBooking(booking);
    setCorrectionTarget(target);
    setCostForm({
      totalCost: booking.totalCost || '',
      contractorCost: booking.contractorCost || '',
      laborHours: booking.laborHours || ''
    });
    setShowCostModal(true);
  };

  const generateInvoiceForBooking = async (booking) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/bookings/${booking.id}/generate-invoice`, {
        method: 'POST',
        headers: apiHeaders
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || `Invoice ${data.invoice?.invoiceNumber} generated`);
        setInvoiceRefreshKey(prev => prev + 1);
        fetchDashboardData();
        setActiveTab('invoices');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error generating invoice');
      }
    } catch (error) {
      toast.error('Error generating invoice');
    }
  };

  // Cost Management - Updated to include contractor cost and labor hours
  const updateBookingFinancials = async () => {
    if (!selectedBooking) {
      toast.error('No booking selected');
      return;
    }
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/bookings/${selectedBooking.id}/financials`, {
        method: 'PUT',
        headers: apiHeaders,
        body: JSON.stringify({
          totalCost: costForm.totalCost ? parseFloat(costForm.totalCost) : null,
          contractorCost: costForm.contractorCost ? parseFloat(costForm.contractorCost) : null,
          laborHours: costForm.laborHours ? parseFloat(costForm.laborHours) : null
        })
      });
      if (response.ok) {
        toast.success('Booking financials updated');
        setShowCostModal(false);
        setCostForm({ totalCost: '', contractorCost: '', laborHours: '' });
        setInvoiceRefreshKey(prev => prev + 1);
        fetchDashboardData();
      }
    } catch (error) {
      toast.error('Error updating financials');
    }
  };

  // Convert chatbot quote to booking
  const convertQuoteToBooking = async () => {
    if (!selectedQuote) {
      toast.error('No quote selected');
      return;
    }
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/chatbot-quotes/${selectedQuote.id}/convert-to-booking`, {
        method: 'POST',
        headers: apiHeaders,
        body: JSON.stringify({
          customerName: convertForm.customerName || null,
          serviceType: convertForm.serviceType || 'General Service',
          preferredDate: convertForm.preferredDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          currentAddress: convertForm.currentAddress || 'To be confirmed',
          newAddress: convertForm.newAddress || null
        })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Booking created for ${data.customerName || selectedQuote.name}!`);
        setShowConvertModal(false);
        setConvertForm({ serviceType: '', preferredDate: '', currentAddress: '', newAddress: '', customerName: '' });
        setSelectedQuote(null);
        fetchDashboardData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error converting quote');
      }
    } catch (error) {
      toast.error('Error converting quote to booking');
    }
  };

  // Database Reset Functions
  const fetchDbStats = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/database-stats`, {
        headers: apiHeaders
      });
      if (response.ok) {
        const data = await response.json();
        setDbStats(data);
      }
    } catch (error) {
      console.error('Error fetching db stats:', error);
    }
  };

  const handleResetDatabase = async () => {
    if (resetConfirmation !== 'DELETE ALL DATA') {
      toast.error('Please type "DELETE ALL DATA" to confirm');
      return;
    }
    
    setIsResetting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/reset-database`, {
        method: 'DELETE',
        headers: apiHeaders,
        body: JSON.stringify({ confirmation: resetConfirmation })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Database reset! Deleted ${data.total_deleted} records.`);
        setShowResetModal(false);
        setResetConfirmation('');
        fetchDashboardData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error resetting database');
      }
    } catch (error) {
      toast.error('Error resetting database');
    } finally {
      setIsResetting(false);
    }
  };

  // Task status transition
  const transitionTaskStatus = async (taskId, newStatus) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/tasks/${taskId}/transition`, {
        method: 'PUT',
        headers: apiHeaders,
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchDashboardData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error transitioning task');
      }
    } catch (error) {
      toast.error('Error updating task status');
    }
  };

  // Delete booking image
  const deleteBookingImage = async (bookingId, imagePath) => {
    if (!window.confirm('Are you sure you want to delete this image? This cannot be undone.')) {
      return;
    }
    
    try {
      // Extract filename from path
      const filename = imagePath.split('/').pop();
      
      // Delete from file storage
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/images/${filename}`, {
        method: 'DELETE',
        headers: apiHeaders
      });
      
      // Remove from booking record
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/bookings/${bookingId}/remove-image`, {
        method: 'PUT',
        headers: apiHeaders,
        body: JSON.stringify({ imagePath })
      });
      
      if (response.ok) {
        toast.success('Image deleted successfully');
        fetchDashboardData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error removing image from booking');
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      toast.error('Error deleting image');
    }
  };

  // Notification management
  const clearAllNotifications = () => {
    const allNotificationKeys = new Set(['contacts', 'bookings', 'tasks', 'payments', 'quotes']);
    setDismissedNotifications(allNotificationKeys);
    setShowNotifications(false);
    toast.success('All notifications cleared');
  };

  const dismissNotification = (key) => {
    setDismissedNotifications(prev => new Set([...prev, key]));
  };

  const getActiveNotifications = () => {
    const notifs = [];
    if (stats.pending_contacts > 0 && !dismissedNotifications.has('contacts')) {
      notifs.push({ key: 'contacts', type: 'warning', icon: 'alert', title: `${stats.pending_contacts} new contact(s) awaiting response`, time: 'Just now' });
    }
    const pendingBookings = bookings.filter(b => b.status === 'pending').length;
    if (pendingBookings > 0 && !dismissedNotifications.has('bookings')) {
      notifs.push({ key: 'bookings', type: 'info', icon: 'calendar', title: `${pendingBookings} pending booking(s) need confirmation`, time: 'Needs attention' });
    }
    const pendingTasks = tasks.filter(t => t.status === 'pending').length;
    if (pendingTasks > 0 && !dismissedNotifications.has('tasks')) {
      notifs.push({ key: 'tasks', type: 'task', icon: 'check', title: `${pendingTasks} task(s) pending`, time: 'Review tasks' });
    }
    const unpaidBookings = bookings.filter(b => {
      const status = getBookingPaymentStatus(b);
      return status.status === 'unpaid' && status.totalCost > 0;
    }).length;
    if (unpaidBookings > 0 && !dismissedNotifications.has('payments')) {
      notifs.push({ key: 'payments', type: 'payment', icon: 'dollar', title: `${unpaidBookings} booking(s) awaiting payment`, time: 'Collect payment' });
    }
    return notifs;
  };

  // Month filter for revenue
  const getMonthOptions = () => {
    const options = [];
    const now = getCurrentEasternParts();
    for (let i = 0; i < 6; i++) {
      const monthDate = new Date(now.year, now.month - 1 - i, 1);
      const monthKey = `${monthDate.getFullYear()}-${String(monthDate.getMonth() + 1).padStart(2, '0')}`;
      const value = i === 0 ? 'current' : monthKey;
      const label = formatEasternMonth(monthKey);
      const shortLabel = label.split(' ')[0].slice(0, 3);
      options.push({ value, label, shortLabel, monthKey });
    }
    return options;
  };

  const filterPaymentsByMonth = (monthValue) => {
    const targetMonth = monthValue === 'current' ? getCurrentEasternParts().monthKey : monthValue;
    return payments.filter(p => getEasternParts(p.createdAt, { timezoneLessIsUtc: true })?.monthKey === targetMonth);
  };

  const getFilteredRevenueStats = () => {
    const filteredPayments = filterPaymentsByMonth(revenueMonth);
    const gross = filteredPayments.reduce((sum, p) => p.paymentType !== 'refund' ? sum + p.amount : sum, 0);
    const refunds = filteredPayments.reduce((sum, p) => p.paymentType === 'refund' ? sum + p.amount : sum, 0);
    
    // Calculate contractor costs for bookings in this period
    const targetMonth = revenueMonth === 'current' ? getCurrentEasternParts().monthKey : revenueMonth;
    const monthBookings = bookings.filter(b => getEasternParts(b.createdAt, { timezoneLessIsUtc: true })?.monthKey === targetMonth);
    const contractorCosts = monthBookings.reduce((sum, b) => sum + (b.contractorCost || 0), 0);
    const laborHours = monthBookings.reduce((sum, b) => sum + (b.laborHours || 0), 0);
    
    return {
      gross: gross - refunds,
      net: gross - refunds - contractorCosts,
      contractorCosts,
      laborHours,
      deposits: filteredPayments.filter(p => p.paymentType === 'deposit').reduce((s, p) => s + p.amount, 0),
      partial: filteredPayments.filter(p => p.paymentType === 'partial').reduce((s, p) => s + p.amount, 0),
      full: filteredPayments.filter(p => p.paymentType === 'full').reduce((s, p) => s + p.amount, 0),
      refunds,
      paymentCount: filteredPayments.length
    };
  };

  // Calculate payment status for a booking
  const getBookingPaymentStatus = (booking) => {
    const bookingPayments = payments.filter(p => p.bookingId === booking.id);
    const totalPaid = bookingPayments.reduce((sum, p) => p.paymentType !== 'refund' ? sum + p.amount : sum, 0);
    const totalRefunded = bookingPayments.reduce((sum, p) => p.paymentType === 'refund' ? sum + p.amount : sum, 0);
    const totalCost = booking.totalCost || 0;
    const contractorCost = booking.contractorCost || 0;
    const laborHours = booking.laborHours || 0;
    const balance = totalCost - totalPaid + totalRefunded;
    const netProfit = totalPaid - totalRefunded - contractorCost;
    
    return {
      totalCost,
      totalPaid,
      totalRefunded,
      contractorCost,
      laborHours,
      balance: Math.max(0, balance),
      netProfit,
      status: totalCost === 0 ? 'no-cost' : balance <= 0 ? 'paid' : totalPaid > 0 ? 'partial' : 'unpaid'
    };
  };

  const getCorrectionHelpText = () => {
    switch (correctionTarget) {
      case 'paid':
        return 'Correct the payment entries for this job. Use this when the amount paid was entered wrong.';
      case 'balance':
        return 'Balance is calculated from Total Cost and Paid. Correct either value here if the balance looks wrong.';
      case 'netProfit':
        return 'Net Profit is calculated from Paid minus Contractor Cost. Update those source values to correct it.';
      case 'contractorCost':
        return 'Update the contractor expense for this job.';
      case 'laborHours':
        return 'Update the labor hours tracked for this job.';
      case 'totalCost':
      default:
        return 'Update the amount the customer was supposed to pay for this job.';
    }
  };

  // Format dates in Eastern Time. Naive service dates are treated as Eastern wall time;
  // UTC/backend timestamps are converted and labeled as EST or EDT automatically.
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = parseAdminDate(dateString);
    if (!date || Number.isNaN(date.getTime())) return 'N/A';

    return date.toLocaleString('en-US', {
      timeZone: EASTERN_TIME_ZONE,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    });
  };

  // Format short date for display
  const formatShortDate = (dateString, options = {}) => {
    if (!dateString) return 'N/A';
    const date = parseAdminDate(dateString, options);
    if (!date || Number.isNaN(date.getTime())) return 'N/A';

    return date.toLocaleDateString('en-US', {
      timeZone: EASTERN_TIME_ZONE,
      month: 'short',
      day: 'numeric'
    });
  };

  // Format createdAt timestamp from the backend in Eastern Time with DST-aware label.
  const formatCreatedAt = (dateString) => {
    if (!dateString) return 'N/A';
    const date = parseAdminDate(dateString, { timezoneLessIsUtc: true });
    if (!date || Number.isNaN(date.getTime())) return 'N/A';

    return date.toLocaleString('en-US', {
      timeZone: EASTERN_TIME_ZONE,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-red-100 text-red-800 border-red-200',
      read: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      replied: 'bg-green-100 text-green-800 border-green-200',
      pending: 'bg-blue-100 text-blue-800 border-blue-200',
      confirmed: 'bg-green-100 text-green-800 border-green-200',
      completed: 'bg-gray-100 text-gray-800 border-gray-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      high: 'text-red-600 bg-red-50',
      medium: 'text-yellow-600 bg-yellow-50',
      low: 'text-green-600 bg-green-50'
    };
    return colors[priority] || 'text-gray-600 bg-gray-50';
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    return { daysInMonth, startingDay };
  };

  const getBookingsForDate = (day) => {
    const selectedMonth = calendarDate.getMonth() + 1;
    const selectedYear = calendarDate.getFullYear();
    return bookings.filter(booking => {
      const bookingDate = getEasternParts(booking.preferredDate);
      return bookingDate?.year === selectedYear && bookingDate?.month === selectedMonth && bookingDate?.day === day;
    });
  };

  // Analytics calculations
  const analytics = useMemo(() => {
    const now = getCurrentEasternParts();
    const lastMonthDate = new Date(now.year, now.month - 2, 1);
    const lastMonthKey = `${lastMonthDate.getFullYear()}-${String(lastMonthDate.getMonth() + 1).padStart(2, '0')}`;
    const thisMonth = bookings.filter(b => getEasternParts(b.createdAt, { timezoneLessIsUtc: true })?.monthKey === now.monthKey);
    const lastMonth = bookings.filter(b => {
      return getEasternParts(b.createdAt, { timezoneLessIsUtc: true })?.monthKey === lastMonthKey;
    });
    
    const movingBookings = bookings.filter(b => b.serviceType?.toLowerCase().includes('moving'));
    const cleaningBookings = bookings.filter(b => b.serviceType?.toLowerCase().includes('cleaning'));
    
    const completedBookings = bookings.filter(b => b.status === 'completed');
    const conversionRate = bookings.length > 0 ? ((completedBookings.length / bookings.length) * 100).toFixed(1) : 0;

    return {
      thisMonthBookings: thisMonth.length,
      lastMonthBookings: lastMonth.length,
      bookingGrowth: lastMonth.length > 0 ? (((thisMonth.length - lastMonth.length) / lastMonth.length) * 100).toFixed(1) : 100,
      movingBookings: movingBookings.length,
      cleaningBookings: cleaningBookings.length,
      conversionRate,
      avgResponseTime: '2.3 hrs',
      chatbotCompletion: chatbotQuotes.length > 0 ? 
        ((chatbotQuotes.filter(q => q.email && q.email !== 'pending@chat.com').length / chatbotQuotes.length) * 100).toFixed(1) : 0
    };
  }, [bookings, chatbotQuotes]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'contacts', label: 'Contacts', icon: MessageCircle, count: contacts.length },
    { id: 'bookings', label: 'Bookings', icon: Calendar, count: bookings.length },
    { id: 'chatbot', label: 'Chatbot', icon: Bot, count: chatbotQuotes.length },
    { id: 'revenue', label: 'Revenue', icon: DollarSign },
    { id: 'invoices', label: 'Invoices', icon: FileText },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'calendar', label: 'Calendar', icon: Calendar },
    { id: 'tasks', label: 'Tasks', icon: CheckSquare, count: tasks.filter(t => t.status !== 'completed').length },
    { id: 'blog', label: 'Blog', icon: BookOpen },
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Header */}
      <header className="bg-white/95 border-b border-slate-200 sticky top-0 z-50 backdrop-blur">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex min-h-16 flex-wrap items-center justify-between gap-3 py-3 lg:flex-nowrap lg:py-0">
            <div className="flex min-w-0 items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_swift-movers-1/artifacts/3vyram8e_Gemini_Generated_Image_tphblstphblstphb.png" 
                alt="Logo" 
                className="h-9 w-auto shrink-0 rounded-md"
              />
              <div className="min-w-0">
                <h1 className="truncate text-base font-bold text-gray-900 sm:text-xl">Swift Move and Clean</h1>
                <p className="text-xs text-gray-500">Admin Dashboard</p>
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-1 sm:gap-2">
              {/* Search */}
              <div className="relative hidden lg:block">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-64 pl-10"
                />
              </div>

              {/* Notifications */}
              <div className="relative">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative"
                >
                  <Bell className="h-5 w-5" />
                  {getActiveNotifications().length > 0 && (
                    <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 rounded-full text-xs text-white flex items-center justify-center">
                      {getActiveNotifications().length}
                    </span>
                  )}
                </Button>
                
                {showNotifications && (
                  <div className="fixed left-4 right-4 top-[116px] max-h-[calc(100vh-136px)] overflow-hidden rounded-lg border border-gray-200 bg-white shadow-2xl z-50 sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-96">
                    <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                      <h3 className="font-semibold text-gray-900">Notifications</h3>
                      {getActiveNotifications().length > 0 && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                          onClick={clearAllNotifications}
                        >
                          <X className="h-3 w-3 mr-1" />
                          Clear All
                        </Button>
                      )}
                    </div>
                    <div className="max-h-[60vh] overflow-y-auto">
                      {getActiveNotifications().length === 0 ? (
                        <div className="p-8 text-center">
                          <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
                          <p className="text-sm text-gray-500">All caught up!</p>
                          <p className="text-xs text-gray-400 mt-1">No pending notifications</p>
                        </div>
                      ) : (
                        getActiveNotifications().map((notif) => (
                          <div key={notif.key} className="p-4 hover:bg-gray-50 border-b border-gray-100 group">
                            <div className="flex items-start space-x-3">
                              <div className={`p-2 rounded-full ${
                                notif.type === 'warning' ? 'bg-yellow-100' :
                                notif.type === 'info' ? 'bg-blue-100' :
                                notif.type === 'task' ? 'bg-purple-100' :
                                'bg-green-100'
                              }`}>
                                {notif.icon === 'alert' && <AlertCircle className={`h-4 w-4 ${notif.type === 'warning' ? 'text-yellow-600' : 'text-gray-600'}`} />}
                                {notif.icon === 'calendar' && <Calendar className="h-4 w-4 text-blue-600" />}
                                {notif.icon === 'check' && <CheckSquare className="h-4 w-4 text-purple-600" />}
                                {notif.icon === 'dollar' && <DollarSign className="h-4 w-4 text-green-600" />}
                              </div>
                              <div className="flex-1">
                                <p className="text-sm text-gray-900">{notif.title}</p>
                                <p className="text-xs text-gray-500 mt-1">{notif.time}</p>
                              </div>
                              <button 
                                onClick={(e) => { e.stopPropagation(); dismissNotification(notif.key); }}
                                className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-gray-600"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    {getActiveNotifications().length > 0 && (
                      <div className="p-3 border-t border-gray-200 bg-gray-50">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="w-full text-sm text-gray-600"
                          onClick={() => { setActiveTab('tasks'); setShowNotifications(false); }}
                        >
                          View All Tasks
                          <ArrowRight className="h-4 w-4 ml-2" />
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Refresh */}
              <Button variant="ghost" size="sm" onClick={fetchDashboardData}>
                <RefreshCw className="h-5 w-5" />
              </Button>

              {/* Reset Database (Danger Zone) */}
              <Button 
                variant="ghost" 
                size="sm" 
                className="hidden text-red-600 hover:text-red-700 hover:bg-red-50 sm:inline-flex"
                onClick={() => { fetchDbStats(); setShowResetModal(true); }}
              >
                <Trash2 className="h-5 w-5" />
              </Button>

              {/* Logout */}
              <Button onClick={onLogout} variant="outline" size="sm" className="px-2 sm:px-3">
                <LogOut className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>

            <div className="relative w-full lg:hidden">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                placeholder="Search admin records..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-11 w-full rounded-lg border-slate-200 bg-slate-50 pl-10"
              />
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar Navigation */}
        <aside className="w-64 bg-white border-r border-gray-200 min-h-[calc(100vh-4rem)] hidden lg:block">
          <nav className="p-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  data-testid={`admin-${item.id}-tab`}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-left transition-colors ${
                    activeTab === item.id
                      ? 'bg-blue-50 text-blue-700 font-medium'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <Icon className="h-5 w-5" />
                    <span>{item.label}</span>
                  </div>
                  {item.count !== undefined && (
                    <Badge variant="secondary" className="bg-gray-100">
                      {item.count}
                    </Badge>
                  )}
                </button>
              );
            })}
          </nav>

          {/* Quick Stats in Sidebar */}
          <div className="p-4 border-t border-gray-200">
            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Quick Stats</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Pending</span>
                <span className="font-semibold text-orange-600">{stats.pending_contacts || 0}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">This Month</span>
                <span className="font-semibold text-blue-600">{analytics.thisMonthBookings}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Completion</span>
                <span className="font-semibold text-green-600">{analytics.conversionRate}%</span>
              </div>
            </div>
          </div>
        </aside>

        {/* Mobile Navigation */}
        <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-slate-200 bg-white/95 shadow-[0_-8px_24px_rgba(15,23,42,0.08)] backdrop-blur lg:hidden">
          <div className="admin-mobile-nav flex gap-1 overflow-x-auto px-3 py-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  data-testid={`admin-mobile-${item.id}-tab`}
                  className={`relative flex min-w-[76px] flex-col items-center rounded-lg px-2 py-2 text-xs font-medium transition-colors ${
                    activeTab === item.id ? 'bg-blue-50 text-blue-700' : 'text-gray-500 hover:bg-slate-50'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="mt-1 leading-none">{item.label}</span>
                  {item.count !== undefined && item.count > 0 && (
                    <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-slate-900 px-1 text-[10px] text-white">
                      {item.count > 99 ? '99+' : item.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Main Content */}
        <main className="min-w-0 flex-1 p-4 pb-24 sm:p-6 lg:pb-6">
          {/* Dashboard Overview */}
          {activeTab === 'dashboard' && (
            <div className="space-y-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">Dashboard Overview</h2>
                <div className="flex space-x-2">
                  <Button variant="outline" size="sm" onClick={() => exportData(bookings, 'bookings')}>
                    <Download className="h-4 w-4 mr-2" />
                    Export
                  </Button>
                </div>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-500">Total Contacts</p>
                        <p className="text-3xl font-bold text-gray-900 mt-1">{stats.total_contacts || 0}</p>
                        <p className="text-xs text-green-600 mt-2 flex items-center">
                          <ArrowUpRight className="h-3 w-3 mr-1" />
                          {stats.recent_contacts || 0} this week
                        </p>
                      </div>
                      <div className="bg-blue-100 p-4 rounded-full">
                        <Users className="h-6 w-6 text-blue-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-500">Total Bookings</p>
                        <p className="text-3xl font-bold text-gray-900 mt-1">{stats.total_bookings || 0}</p>
                        <p className="text-xs text-green-600 mt-2 flex items-center">
                          <ArrowUpRight className="h-3 w-3 mr-1" />
                          {analytics.bookingGrowth}% growth
                        </p>
                      </div>
                      <div className="bg-green-100 p-4 rounded-full">
                        <Calendar className="h-6 w-6 text-green-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-500">Chatbot Quotes</p>
                        <p className="text-3xl font-bold text-gray-900 mt-1">{stats.chatbot_quotes || 0}</p>
                        <p className="text-xs text-blue-600 mt-2 flex items-center">
                          <Zap className="h-3 w-3 mr-1" />
                          {analytics.chatbotCompletion}% completion
                        </p>
                      </div>
                      <div className="bg-orange-100 p-4 rounded-full">
                        <Bot className="h-6 w-6 text-orange-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-500">Pending Items</p>
                        <p className="text-3xl font-bold text-gray-900 mt-1">{stats.pending_contacts || 0}</p>
                        <p className="text-xs text-red-600 mt-2 flex items-center">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          Needs attention
                        </p>
                      </div>
                      <div className="bg-red-100 p-4 rounded-full">
                        <AlertCircle className="h-6 w-6 text-red-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                {/* Service Distribution */}
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg">Service Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-gray-600">Moving Services</span>
                          <span className="text-sm font-semibold">{analytics.movingBookings}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full" 
                            style={{ width: `${bookings.length > 0 ? (analytics.movingBookings / bookings.length) * 100 : 0}%` }}
                          ></div>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-gray-600">Cleaning Services</span>
                          <span className="text-sm font-semibold">{analytics.cleaningBookings}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-green-600 h-2 rounded-full" 
                            style={{ width: `${bookings.length > 0 ? (analytics.cleaningBookings / bookings.length) * 100 : 0}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Recent Activity */}
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg">Recent Activity</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {[...contacts, ...bookings]
                        .sort((a, b) => parseAdminDate(b.createdAt, { timezoneLessIsUtc: true }) - parseAdminDate(a.createdAt, { timezoneLessIsUtc: true }))
                        .slice(0, 5)
                        .map((item, index) => (
                          <div key={index} className="flex items-center space-x-3">
                            <div className={`p-2 rounded-full ${item.serviceType ? 'bg-green-100' : 'bg-blue-100'}`}>
                              {item.serviceType ? (
                                <Calendar className="h-3 w-3 text-green-600" />
                              ) : (
                                <MessageCircle className="h-3 w-3 text-blue-600" />
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
                              <p className="text-xs text-gray-500">{formatShortDate(item.createdAt, { timezoneLessIsUtc: true })}</p>
                            </div>
                            <Badge className={getStatusColor(item.status)} variant="outline">
                              {item.status}
                            </Badge>
                          </div>
                        ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Quick Actions */}
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg">Quick Actions</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Button className="w-full justify-start" variant="outline" onClick={() => setActiveTab('contacts')}>
                      <MessageCircle className="h-4 w-4 mr-3" />
                      View All Contacts
                    </Button>
                    <Button className="w-full justify-start" variant="outline" onClick={() => setActiveTab('bookings')}>
                      <Calendar className="h-4 w-4 mr-3" />
                      Manage Bookings
                    </Button>
                    <Button className="w-full justify-start" variant="outline" onClick={() => setActiveTab('chatbot')}>
                      <Bot className="h-4 w-4 mr-3" />
                      Review Chatbot Quotes
                    </Button>
                    <Button className="w-full justify-start bg-blue-600 text-white hover:bg-blue-700" onClick={() => exportData([...contacts, ...bookings], 'all_data')}>
                      <Download className="h-4 w-4 mr-3" />
                      Export All Data
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Upcoming Appointments Preview */}
              <Card className="border-0 shadow-sm">
                <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <CardTitle className="text-lg">Upcoming Appointments</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setActiveTab('calendar')}>
                    View Calendar
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    {bookings
                      .filter(b => parseAdminDate(b.preferredDate) >= new Date() && b.status !== 'cancelled')
                      .sort((a, b) => parseAdminDate(a.preferredDate) - parseAdminDate(b.preferredDate))
                      .slice(0, 3)
                      .map((booking, index) => (
                        <div key={index} className="p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-2 mb-2">
                            <Calendar className="h-4 w-4 text-blue-600" />
                            <span className="text-sm font-medium">{formatShortDate(booking.preferredDate)}</span>
                          </div>
                          <p className="font-semibold text-gray-900">{booking.name}</p>
                          <p className="text-sm text-gray-600">{booking.serviceType}</p>
                          <Badge className={`${getStatusColor(booking.status)} mt-2`} variant="outline">
                            {booking.status}
                          </Badge>
                        </div>
                      ))}
                    {bookings.filter(b => parseAdminDate(b.preferredDate) >= new Date()).length === 0 && (
                      <p className="text-gray-500 md:col-span-3 text-center py-4">No upcoming appointments</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Contacts Tab */}
          {activeTab === 'contacts' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">Contact Messages</h2>
                  <p className="text-sm text-gray-500 mt-1">Submissions from the "Send us a message" contact form</p>
                </div>
                <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-full sm:w-32">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="read">Read</SelectItem>
                      <SelectItem value="replied">Replied</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" onClick={() => exportData(filteredContacts, 'contacts')} className="w-full sm:w-auto">
                    <Download className="h-4 w-4 mr-2" />
                    Export
                  </Button>
                </div>
              </div>

              <div className="space-y-4">
                {filteredContacts.length === 0 ? (
                  <Card className="border-0 shadow-sm">
                    <CardContent className="p-12 text-center">
                      <MessageCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No contact form submissions</p>
                      <p className="text-xs text-gray-400 mt-2">Messages from the website contact form will appear here</p>
                    </CardContent>
                  </Card>
                ) : (
                  filteredContacts.map((contact) => (
                    <Card key={contact.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                      <CardContent className="p-4 sm:p-6">
                        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div className="flex min-w-0 items-center space-x-3">
                            <div className="bg-blue-100 p-2 rounded-full">
                              <MessageCircle className="h-4 w-4 text-blue-600" />
                            </div>
                            <div className="min-w-0">
                              <h3 className="font-semibold text-gray-900">{contact.name}</h3>
                              <p className="text-sm text-gray-500">{formatCreatedAt(contact.createdAt)}</p>
                            </div>
                          </div>
                          <Badge className={getStatusColor(contact.status)} variant="outline">
                            {contact.status}
                          </Badge>
                        </div>

                        <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
                          <div className="flex min-w-0 items-center space-x-2">
                            <Mail className="h-4 w-4 shrink-0 text-gray-400" />
                            <span className="break-all text-sm">{contact.email}</span>
                          </div>
                          {contact.phone && (
                            <div className="flex min-w-0 items-center space-x-2">
                              <Phone className="h-4 w-4 shrink-0 text-gray-400" />
                              <span className="text-sm">{contact.phone}</span>
                            </div>
                          )}
                          <div className="flex min-w-0 items-center space-x-2">
                            <FileText className="h-4 w-4 shrink-0 text-gray-400" />
                            <span className="break-words text-sm capitalize">{contact.subject}</span>
                          </div>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg mb-4">
                          <p className="text-gray-700 whitespace-pre-wrap">{contact.message}</p>
                        </div>

                        <div className="grid grid-cols-1 gap-2 sm:flex sm:flex-wrap">
                          {contact.status === 'new' && (
                            <Button size="sm" variant="outline" onClick={() => updateContactStatus(contact.id, 'read')}>
                              <Eye className="h-4 w-4 mr-2" />
                              Mark as Read
                            </Button>
                          )}
                          {contact.status !== 'replied' && (
                            <Button size="sm" className="bg-green-600 text-white hover:bg-green-700" onClick={() => updateContactStatus(contact.id, 'replied')}>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Mark as Replied
                            </Button>
                          )}
                          {contact.phone && (
                            <Button size="sm" variant="outline" onClick={() => window.open(`tel:${contact.phone}`, '_self')}>
                              <Phone className="h-4 w-4 mr-2" />
                              Call
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => window.open(`mailto:${contact.email}`, '_self')}>
                            <Mail className="h-4 w-4 mr-2" />
                            Email
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Bookings Tab */}
          {activeTab === 'bookings' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">Service Bookings</h2>
                  <p className="text-sm text-gray-500 mt-1">Submissions from the "Book your service" form</p>
                </div>
                <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-full sm:w-32">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filterService} onValueChange={setFilterService}>
                    <SelectTrigger className="w-full sm:w-32">
                      <SelectValue placeholder="Service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Services</SelectItem>
                      <SelectItem value="moving">Moving</SelectItem>
                      <SelectItem value="cleaning">Cleaning</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" onClick={() => exportData(filteredBookings, 'bookings')} className="col-span-2 w-full sm:col-span-1 sm:w-auto">
                    <Download className="h-4 w-4 mr-2" />
                    Export
                  </Button>
                </div>
              </div>

              <div className="space-y-4">
                {filteredBookings.length === 0 ? (
                  <Card className="border-0 shadow-sm">
                    <CardContent className="p-12 text-center">
                      <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No service bookings found</p>
                      <p className="text-xs text-gray-400 mt-2">Submissions from the booking form will appear here</p>
                    </CardContent>
                  </Card>
                ) : (
                  filteredBookings.map((booking) => (
                    <Card key={booking.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                      <CardContent className="p-4 sm:p-6">
                        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div className="flex min-w-0 items-center space-x-3">
                            <div className="bg-green-100 p-2 rounded-full">
                              <Calendar className="h-4 w-4 text-green-600" />
                            </div>
                            <div className="min-w-0">
                              <h3 className="font-semibold text-gray-900">{booking.name}</h3>
                              <p className="text-sm text-gray-500">Booked: {formatCreatedAt(booking.createdAt)}</p>
                            </div>
                          </div>
                          <Badge className={getStatusColor(booking.status)} variant="outline">
                            {booking.status}
                          </Badge>
                        </div>

                        <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                          <div className="flex min-w-0 items-center space-x-2">
                            <Mail className="h-4 w-4 shrink-0 text-gray-400" />
                            <span className="break-all text-sm">{booking.email}</span>
                          </div>
                          <div className="flex min-w-0 items-center space-x-2">
                            <Phone className="h-4 w-4 shrink-0 text-gray-400" />
                            <span className="text-sm">{booking.phone}</span>
                          </div>
                          <div className="flex min-w-0 items-center space-x-2">
                            <Calendar className="h-4 w-4 shrink-0 text-gray-400" />
                            <span className="text-sm font-medium">{formatDate(booking.preferredDate)}</span>
                          </div>
                          <div className="flex min-w-0 items-center space-x-2">
                            {booking.serviceType?.toLowerCase().includes('moving') ? (
                              <Briefcase className="h-4 w-4 shrink-0 text-gray-400" />
                            ) : (
                              <Sparkles className="h-4 w-4 shrink-0 text-gray-400" />
                            )}
                            <span className="break-words text-sm">{booking.serviceType}</span>
                          </div>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg mb-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <p className="text-sm font-medium text-gray-700 mb-1">From:</p>
                              <p className="text-sm text-gray-600">{booking.currentAddress}</p>
                            </div>
                            {booking.newAddress && (
                              <div>
                                <p className="text-sm font-medium text-gray-700 mb-1">To:</p>
                                <p className="text-sm text-gray-600">{booking.newAddress}</p>
                              </div>
                            )}
                          </div>
                          {booking.specialRequests && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <p className="text-sm font-medium text-gray-700 mb-1">Special Requests:</p>
                              <p className="text-sm text-gray-600">{booking.specialRequests}</p>
                            </div>
                          )}
                          {booking.images && booking.images.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <p className="text-sm font-medium text-gray-700 mb-2">Uploaded Photos ({booking.images.length}):</p>
                              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                                {booking.images.map((imagePath, idx) => (
                                  <div key={idx} className="relative group">
                                    <a 
                                      href={`${process.env.REACT_APP_BACKEND_URL}${imagePath}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="block"
                                    >
                                      <img
                                        src={`${process.env.REACT_APP_BACKEND_URL}${imagePath}`}
                                        alt={`Booking photo ${idx + 1}`}
                                        className="w-full h-20 object-cover rounded border border-gray-200 hover:border-blue-500 transition-colors cursor-pointer"
                                      />
                                    </a>
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        deleteBookingImage(booking.id, imagePath);
                                      }}
                                      className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                                      title="Delete image"
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                              <p className="text-xs text-gray-400 mt-2">Tap a photo to open it. Use the corner button to delete.</p>
                            </div>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
                          {booking.status === 'pending' && (
                            <Button size="sm" className="bg-green-600 text-white hover:bg-green-700" onClick={() => updateBookingStatus(booking.id, 'confirmed')}>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Confirm
                            </Button>
                          )}
                          {booking.status === 'confirmed' && (
                            <Button size="sm" className="bg-gray-600 text-white hover:bg-gray-700" onClick={() => updateBookingStatus(booking.id, 'completed')}>
                              <CheckSquare className="h-4 w-4 mr-2" />
                              Complete
                            </Button>
                          )}
                          {booking.status !== 'cancelled' && booking.status !== 'completed' && (
                            <Button size="sm" variant="outline" className="border-red-500 text-red-500 hover:bg-red-50" onClick={() => updateBookingStatus(booking.id, 'cancelled')}>
                              <X className="h-4 w-4 mr-2" />
                              Cancel
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => window.open(`tel:${booking.phone}`, '_self')}>
                            <Phone className="h-4 w-4 mr-2" />
                            Call
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Chatbot Tab */}
          {activeTab === 'chatbot' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">Chatbot Quote Requests</h2>
                <Button variant="outline" onClick={() => exportData(filteredChatbotQuotes, 'chatbot_quotes')} className="w-full sm:w-auto">
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </div>

              {/* Chatbot Metrics */}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-center space-x-3">
                      <div className="bg-green-100 p-3 rounded-full">
                        <Target className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Completion Rate</p>
                        <p className="text-2xl font-bold">{analytics.chatbotCompletion}%</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-center space-x-3">
                      <div className="bg-blue-100 p-3 rounded-full">
                        <Clock className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Avg Response Time</p>
                        <p className="text-2xl font-bold">{analytics.avgResponseTime}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-center space-x-3">
                      <div className="bg-orange-100 p-3 rounded-full">
                        <Bot className="h-5 w-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Total Conversations</p>
                        <p className="text-2xl font-bold">{chatbotQuotes.length}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-4">
                {filteredChatbotQuotes.length === 0 ? (
                  <Card className="border-0 shadow-sm">
                    <CardContent className="p-12 text-center">
                      <Bot className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No chatbot quotes found</p>
                    </CardContent>
                  </Card>
                ) : (
                  filteredChatbotQuotes.map((quote) => (
                    <Card key={quote.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                      <CardContent className="p-4 sm:p-6">
                        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div className="flex min-w-0 items-center space-x-3">
                            <div className="bg-orange-100 p-2 rounded-full">
                              <Bot className="h-4 w-4 text-orange-600" />
                            </div>
                            <div className="min-w-0">
                              <h3 className="font-semibold text-gray-900">{quote.name}</h3>
                              <p className="text-sm text-gray-500">{formatCreatedAt(quote.createdAt)}</p>
                            </div>
                          </div>
                          <Badge className="bg-orange-100 text-orange-800 border-orange-200" variant="outline">
                            Chatbot
                          </Badge>
                        </div>

                        <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                          <div className="flex min-w-0 items-center space-x-2">
                            <Mail className="h-4 w-4 shrink-0 text-gray-400" />
                            <span className="break-all text-sm">{quote.email || 'Not provided'}</span>
                          </div>
                          <div className="flex min-w-0 items-center space-x-2">
                            <Phone className="h-4 w-4 shrink-0 text-gray-400" />
                            <span className="text-sm">{quote.phone || 'Not provided'}</span>
                          </div>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg mb-4">
                          <p className="text-sm font-medium text-gray-700 mb-2">Conversation Summary:</p>
                          <p className="text-gray-700 whitespace-pre-wrap text-sm">{quote.message}</p>
                        </div>

                        {quote.sessionId && (
                          <p className="text-xs text-gray-400 mb-4">Session: {quote.sessionId}</p>
                        )}

                        <div className="grid grid-cols-1 gap-2 sm:flex sm:flex-wrap">
                          <Button size="sm" className="bg-green-600 text-white hover:bg-green-700" onClick={() => {
                            setSelectedQuote(quote);
                            setShowConvertModal(true);
                          }}>
                            <ArrowRight className="h-4 w-4 mr-2" />
                            Convert to Booking
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => updateContactStatus(quote.id, 'contacted')}>
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Mark Contacted
                          </Button>
                          {quote.phone && quote.phone !== 'Pending' && (
                            <Button size="sm" variant="outline" onClick={() => window.open(`tel:${quote.phone}`, '_self')}>
                              <Phone className="h-4 w-4 mr-2" />
                              Call
                            </Button>
                          )}
                          {quote.email && quote.email !== 'pending@chat.com' && (
                            <Button size="sm" variant="outline" onClick={() => window.open(`mailto:${quote.email}`, '_self')}>
                              <Mail className="h-4 w-4 mr-2" />
                              Email
                            </Button>
                          )}
                          {quote.convertedToBooking && (
                            <Badge className="bg-blue-100 text-blue-800">Converted to booking</Badge>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">Analytics & Insights</h2>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <Card className="border-0 shadow-sm">
                  <CardContent className="p-4 sm:p-6">
                    <p className="text-sm text-gray-500">This Month Bookings</p>
                    <p className="text-3xl font-bold text-gray-900 mt-1">{analytics.thisMonthBookings}</p>
                    <div className={`text-sm mt-2 flex items-center ${Number(analytics.bookingGrowth) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {Number(analytics.bookingGrowth) >= 0 ? <ArrowUpRight className="h-4 w-4 mr-1" /> : <ArrowDownRight className="h-4 w-4 mr-1" />}
                      {analytics.bookingGrowth}% vs last month
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-sm">
                  <CardContent className="p-4 sm:p-6">
                    <p className="text-sm text-gray-500">Conversion Rate</p>
                    <p className="text-3xl font-bold text-gray-900 mt-1">{analytics.conversionRate}%</p>
                    <p className="text-sm text-gray-500 mt-2">Bookings completed</p>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-sm">
                  <CardContent className="p-4 sm:p-6">
                    <p className="text-sm text-gray-500">Chatbot Efficiency</p>
                    <p className="text-3xl font-bold text-gray-900 mt-1">{analytics.chatbotCompletion}%</p>
                    <p className="text-sm text-gray-500 mt-2">Info collection rate</p>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-sm">
                  <CardContent className="p-4 sm:p-6">
                    <p className="text-sm text-gray-500">Response Time</p>
                    <p className="text-3xl font-bold text-gray-900 mt-1">{analytics.avgResponseTime}</p>
                    <p className="text-sm text-gray-500 mt-2">Average</p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle>Booking Status Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {['pending', 'confirmed', 'completed', 'cancelled'].map(status => {
                        const count = bookings.filter(b => b.status === status).length;
                        const percentage = bookings.length > 0 ? ((count / bookings.length) * 100).toFixed(1) : 0;
                        return (
                          <div key={status}>
                            <div className="flex justify-between mb-1">
                              <span className="text-sm capitalize text-gray-600">{status}</span>
                              <span className="text-sm font-medium">{count} ({percentage}%)</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full ${
                                  status === 'pending' ? 'bg-blue-500' :
                                  status === 'confirmed' ? 'bg-green-500' :
                                  status === 'completed' ? 'bg-gray-500' : 'bg-red-500'
                                }`}
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-sm">
                  <CardHeader>
                    <CardTitle>Contact Status Overview</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {['new', 'read', 'replied'].map(status => {
                        const count = contacts.filter(c => c.status === status).length;
                        const percentage = contacts.length > 0 ? ((count / contacts.length) * 100).toFixed(1) : 0;
                        return (
                          <div key={status}>
                            <div className="flex justify-between mb-1">
                              <span className="text-sm capitalize text-gray-600">{status}</span>
                              <span className="text-sm font-medium">{count} ({percentage}%)</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full ${
                                  status === 'new' ? 'bg-red-500' :
                                  status === 'read' ? 'bg-yellow-500' : 'bg-green-500'
                                }`}
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Revenue Tab */}
          {activeTab === 'revenue' && (
            <div className="space-y-6">
              <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">Revenue & Payments</h2>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  {/* Month Filter */}
                  <div className="admin-mobile-nav flex items-center gap-1 overflow-x-auto rounded-lg bg-gray-100 p-1">
                    {getMonthOptions().slice(0, 4).map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setRevenueMonth(option.value)}
                        className={`shrink-0 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                          revenueMonth === option.value
                            ? 'bg-white text-blue-600 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        {option.value === 'current' ? 'This Month' : option.shortLabel}
                      </button>
                    ))}
                    <Select value={revenueMonth} onValueChange={setRevenueMonth}>
                      <SelectTrigger className="h-8 w-[130px] shrink-0 border-0 bg-transparent text-sm">
                        <Filter className="h-4 w-4 mr-1" />
                        <SelectValue placeholder="More..." />
                      </SelectTrigger>
                      <SelectContent>
                        {getMonthOptions().map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button variant="outline" onClick={() => exportData(payments, 'payments')} className="w-full sm:w-auto">
                    <Download className="h-4 w-4 mr-2" />
                    Export
                  </Button>
                </div>
              </div>

              {/* Month Indicator */}
              <div className="flex flex-wrap items-center gap-2">
                <Calendar className="h-4 w-4 shrink-0 text-gray-500" />
                <span className="text-sm text-gray-600">
                  Showing data for: <span className="font-medium text-gray-900">
                    {revenueMonth === 'current' 
                      ? formatEasternMonth(getCurrentEasternParts().monthKey)
                      : formatEasternMonth(revenueMonth)
                    }
                  </span>
                </span>
                <Badge variant="secondary" className="text-xs">
                  {getFilteredRevenueStats().paymentCount} payments
                </Badge>
              </div>

              {/* Revenue Stats - Dynamic based on filter */}
              {(() => {
                const stats = getFilteredRevenueStats();
                return (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
                    <Card className="border-0 shadow-sm bg-gradient-to-br from-green-50 to-white">
                      <CardContent className="p-4 sm:p-5">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-medium text-gray-500">Net Revenue</p>
                            <p className="text-2xl font-bold text-green-600 mt-1">${stats.net.toLocaleString()}</p>
                            <p className="text-xs text-gray-500 mt-1">After costs</p>
                          </div>
                          <div className="bg-green-100 p-3 rounded-full">
                            <DollarSign className="h-5 w-5 text-green-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-0 shadow-sm">
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-medium text-gray-500">Gross Revenue</p>
                            <p className="text-2xl font-bold text-blue-600 mt-1">${stats.gross.toLocaleString()}</p>
                            <p className="text-xs text-gray-500 mt-1">Before costs</p>
                          </div>
                          <div className="bg-blue-100 p-3 rounded-full">
                            <TrendingUp className="h-5 w-5 text-blue-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-0 shadow-sm">
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-medium text-gray-500">Contractor Costs</p>
                            <p className="text-2xl font-bold text-red-600 mt-1">-${stats.contractorCosts.toLocaleString()}</p>
                            <p className="text-xs text-gray-500 mt-1">Expenses</p>
                          </div>
                          <div className="bg-red-100 p-3 rounded-full">
                            <Briefcase className="h-5 w-5 text-red-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-0 shadow-sm">
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-medium text-gray-500">Outstanding</p>
                            <p className="text-2xl font-bold text-orange-600 mt-1">${revenueSummary.outstandingBalance?.toLocaleString() || 0}</p>
                            <p className="text-xs text-gray-500 mt-1">To collect</p>
                          </div>
                          <div className="bg-orange-100 p-3 rounded-full">
                            <AlertCircle className="h-5 w-5 text-orange-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-0 shadow-sm">
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs font-medium text-gray-500">Labor Hours</p>
                            <p className="text-2xl font-bold text-purple-600 mt-1">{stats.laborHours.toLocaleString()}h</p>
                            <p className="text-xs text-gray-500 mt-1">Tracked</p>
                          </div>
                          <div className="bg-purple-100 p-3 rounded-full">
                            <Clock className="h-5 w-5 text-purple-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                );
              })()}

              {/* Payment Breakdown - Dynamic */}
              {(() => {
                const stats = getFilteredRevenueStats();
                return (
                  <Card className="border-0 shadow-sm">
                    <CardHeader>
                      <CardTitle className="text-lg">Payment Breakdown</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 gap-3 text-center sm:grid-cols-3 xl:grid-cols-5">
                        <div className="p-3 bg-green-50 rounded-lg">
                          <p className="text-xs text-gray-500">Deposits</p>
                          <p className="text-lg font-bold text-green-600">${stats.deposits.toLocaleString()}</p>
                        </div>
                        <div className="p-3 bg-blue-50 rounded-lg">
                          <p className="text-xs text-gray-500">Partial</p>
                          <p className="text-lg font-bold text-blue-600">${stats.partial.toLocaleString()}</p>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-xs text-gray-500">Full</p>
                          <p className="text-lg font-bold text-gray-600">${stats.full.toLocaleString()}</p>
                        </div>
                        <div className="p-3 bg-red-50 rounded-lg">
                          <p className="text-xs text-gray-500">Refunds</p>
                          <p className="text-lg font-bold text-red-600">-${stats.refunds.toLocaleString()}</p>
                        </div>
                        <div className="p-3 bg-orange-50 rounded-lg">
                          <p className="text-xs text-gray-500">Contractor</p>
                          <p className="text-lg font-bold text-orange-600">-${stats.contractorCosts.toLocaleString()}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Bookings with Payment Status */}
              <Card className="border-0 shadow-sm">
                <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <CardTitle className="text-lg">Booking Payment Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {bookings.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">No bookings found</p>
                    ) : (
                      bookings.map(booking => {
                        const paymentStatus = getBookingPaymentStatus(booking);
                        const hasInvoice = bookingHasInvoice(booking.id);
                        return (
                          <div key={booking.id} className="rounded-lg bg-gray-50 p-3 sm:p-4">
                            <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                              <div className="min-w-0">
                                <h4 className="font-semibold text-gray-900">{booking.name}</h4>
                                <p className="text-sm text-gray-500">{booking.serviceType} - {formatShortDate(booking.preferredDate)}</p>
                              </div>
                              <div className="flex flex-wrap items-center gap-2">
                                <Badge className={getStatusColor(booking.status)} variant="outline">{booking.status}</Badge>
                                <Badge className={
                                  paymentStatus.status === 'paid' ? 'bg-green-100 text-green-800' :
                                  paymentStatus.status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                                  paymentStatus.status === 'unpaid' ? 'bg-red-100 text-red-800' :
                                  'bg-gray-100 text-gray-800'
                                }>
                                  {paymentStatus.status === 'no-cost' ? 'No cost set' : paymentStatus.status}
                                </Badge>
                              </div>
                            </div>
                            <div className="mb-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-3 xl:grid-cols-6">
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-500">Total Cost:</span>
                                  <button
                                    type="button"
                                    className="text-gray-400 hover:text-sky-blue"
                                    onClick={() => openFinancialsModal(booking, 'totalCost')}
                                    aria-label={`Edit total cost for ${booking.name}`}
                                  >
                                    <Edit className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                                <p className="font-semibold">${paymentStatus.totalCost.toLocaleString()}</p>
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-500">Paid:</span>
                                  <button
                                    type="button"
                                    className="text-gray-400 hover:text-sky-blue"
                                    onClick={() => openPaymentCorrectionModal(booking, 'paid')}
                                    aria-label={`Edit paid amount for ${booking.name}`}
                                  >
                                    <Edit className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                                <p className="font-semibold text-green-600">${paymentStatus.totalPaid.toLocaleString()}</p>
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-500">Contractor:</span>
                                  <button
                                    type="button"
                                    className="text-gray-400 hover:text-sky-blue"
                                    onClick={() => openFinancialsModal(booking, 'contractorCost')}
                                    aria-label={`Edit contractor cost for ${booking.name}`}
                                  >
                                    <Edit className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                                <p className="font-semibold text-red-600">-${paymentStatus.contractorCost.toLocaleString()}</p>
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-500">Net Profit:</span>
                                  <button
                                    type="button"
                                    className="text-gray-400 hover:text-sky-blue"
                                    onClick={() => openPaymentCorrectionModal(booking, 'netProfit')}
                                    aria-label={`Adjust net profit inputs for ${booking.name}`}
                                  >
                                    <Edit className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                                <p className={`font-semibold ${paymentStatus.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>${paymentStatus.netProfit.toLocaleString()}</p>
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-500">Balance:</span>
                                  <button
                                    type="button"
                                    className="text-gray-400 hover:text-sky-blue"
                                    onClick={() => openPaymentCorrectionModal(booking, 'balance')}
                                    aria-label={`Adjust balance inputs for ${booking.name}`}
                                  >
                                    <Edit className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                                <p className="font-semibold text-orange-600">${paymentStatus.balance.toLocaleString()}</p>
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-500">Hours:</span>
                                  <button
                                    type="button"
                                    className="text-gray-400 hover:text-sky-blue"
                                    onClick={() => openFinancialsModal(booking, 'laborHours')}
                                    aria-label={`Edit labor hours for ${booking.name}`}
                                  >
                                    <Edit className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                                <p className="font-semibold text-purple-600">{paymentStatus.laborHours || 0}h</p>
                              </div>
                            </div>
                            <div className="grid grid-cols-1 gap-2 sm:flex sm:flex-wrap">
                              <Button size="sm" variant="outline" onClick={() => openFinancialsModal(booking, 'totalCost')}>
                                <Edit className="h-3 w-3 mr-1" />
                                Set Costs
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => openPaymentCorrectionModal(booking, 'paid')}>
                                <Eye className="h-3 w-3 mr-1" />
                                Edit Payments
                              </Button>
                              <Button size="sm" className="bg-green-600 text-white hover:bg-green-700" onClick={() => openPaymentModalForBooking(booking)}>
                                <Plus className="h-3 w-3 mr-1" />
                                Log Payment
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => { setSelectedBooking(booking); setShowTaskModal(true); }}>
                                <CheckSquare className="h-3 w-3 mr-1" />
                                Add Task
                              </Button>
                              {booking.status === 'completed' && paymentStatus.totalPaid > 0 && !hasInvoice && (
                                <Button size="sm" variant="outline" onClick={() => generateInvoiceForBooking(booking)}>
                                  <FileText className="h-3 w-3 mr-1" />
                                  Generate Invoice
                                </Button>
                              )}
                              {hasInvoice && (
                                <Badge className="bg-emerald-100 text-emerald-800">
                                  Invoice Ready
                                </Badge>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Recent Payments */}
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg">Recent Payments</CardTitle>
                </CardHeader>
                <CardContent>
                  {payments.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No payments logged yet</p>
                  ) : (
                    <div className="space-y-3">
                      {payments.slice(0, 10).map(payment => {
                        const booking = bookings.find(b => b.id === payment.bookingId);
                        return (
                          <div key={payment.id} className="flex flex-col gap-3 rounded-lg bg-gray-50 p-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex min-w-0 items-center gap-3">
                              <div className={`p-2 rounded-full ${payment.paymentType === 'refund' ? 'bg-red-100' : 'bg-green-100'}`}>
                                <DollarSign className={`h-4 w-4 ${payment.paymentType === 'refund' ? 'text-red-600' : 'text-green-600'}`} />
                              </div>
                              <div className="min-w-0">
                                <p className="font-medium text-gray-900">{booking?.name || 'Unknown Booking'}</p>
                                <p className="text-xs text-gray-500">
                                  {payment.paymentType} - {payment.paymentMethod} - {formatShortDate(payment.createdAt, { timezoneLessIsUtc: true })}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center justify-between gap-3 sm:justify-end">
                              <span className={`font-semibold ${payment.paymentType === 'refund' ? 'text-red-600' : 'text-green-600'}`}>
                                {payment.paymentType === 'refund' ? '-' : '+'}${payment.amount.toLocaleString()}
                              </span>
                              <Button size="sm" variant="ghost" onClick={() => openPaymentModalForEdit(payment, booking)}>
                                <Edit className="h-4 w-4 text-gray-400 hover:text-blue-500" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => deletePayment(payment.id)}>
                                <Trash2 className="h-4 w-4 text-gray-400 hover:text-red-500" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === 'invoices' && (
            <InvoiceTab token={token} refreshKey={invoiceRefreshKey} />
          )}

          {/* Calendar Tab */}
          {activeTab === 'calendar' && (
            <div className="space-y-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">Appointment Calendar</h2>
                <div className="flex items-center justify-between gap-2 sm:justify-end">
                  <Button variant="outline" size="sm" onClick={() => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() - 1, 1))}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="font-medium min-w-[150px] text-center">
                    {formatEasternMonth(`${calendarDate.getFullYear()}-${String(calendarDate.getMonth() + 1).padStart(2, '0')}`)}
                  </span>
                  <Button variant="outline" size="sm" onClick={() => setCalendarDate(new Date(calendarDate.getFullYear(), calendarDate.getMonth() + 1, 1))}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <Card className="border-0 shadow-sm">
                <CardContent className="p-3 sm:p-6">
                  <div className="admin-mobile-nav overflow-x-auto">
                    <div className="min-w-[640px]">
                      <div className="grid grid-cols-7 gap-1 mb-2">
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                          <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                            {day}
                          </div>
                        ))}
                      </div>
                      <div className="grid grid-cols-7 gap-1">
                        {(() => {
                          const { daysInMonth, startingDay } = getDaysInMonth(calendarDate);
                          const days = [];
                          for (let i = 0; i < startingDay; i++) {
                            days.push(<div key={`empty-${i}`} className="p-2"></div>);
                          }
                          for (let day = 1; day <= daysInMonth; day++) {
                            const dayBookings = getBookingsForDate(day);
                            const isToday = isSameEasternDay(
                              easternWallTimeToDate({ year: calendarDate.getFullYear(), month: calendarDate.getMonth() + 1, day }),
                              getCurrentEasternParts()
                            );
                            days.push(
                              <div
                                key={day}
                                className={`min-h-[86px] rounded-lg border p-2 ${isToday ? 'bg-blue-50 border-blue-200' : 'border-gray-100 bg-white'}`}
                              >
                                <span className={`text-sm font-medium ${isToday ? 'text-blue-600' : 'text-gray-700'}`}>
                                  {day}
                                </span>
                                {dayBookings.slice(0, 2).map((booking, idx) => (
                                  <div key={idx} className="mt-1 truncate rounded bg-green-100 px-1 py-0.5 text-xs text-green-800">
                                    {booking.name}
                                  </div>
                                ))}
                                {dayBookings.length > 2 && (
                                  <div className="text-xs text-gray-500 mt-1">+{dayBookings.length - 2} more</div>
                                )}
                              </div>
                            );
                          }
                          return days;
                        })()}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Tasks Tab */}
          {activeTab === 'tasks' && (
            <div className="space-y-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">Task Manager</h2>
                <Button size="sm" onClick={() => { setSelectedBooking(null); setShowTaskModal(true); }} className="w-full sm:w-auto">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Task
                </Button>
              </div>

              <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                {/* Pending Tasks */}
                <Card className="border-0 shadow-sm border-t-4 border-t-yellow-500">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center justify-between">
                      <span className="flex items-center">
                        <div className="h-2 w-2 bg-yellow-500 rounded-full mr-2"></div>
                        Pending
                      </span>
                      <Badge variant="secondary">{tasks.filter(t => t.status === 'pending').length}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 max-h-[600px] overflow-y-auto">
                    {tasks.filter(t => t.status === 'pending').map(task => (
                      <div key={task.id} className={`p-4 rounded-lg border ${task.autoGenerated ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'} shadow-sm`}>
                        <div className="flex items-start justify-between mb-2">
                          <p className="text-sm font-semibold text-gray-900">{task.title}</p>
                          <button onClick={() => deleteTask(task.id)} className="text-gray-400 hover:text-red-500">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        {task.description && <p className="text-xs text-gray-600 mb-2">{task.description}</p>}
                        
                        {/* Contact & Service Context */}
                        {(task.contactName || task.serviceType) && (
                          <div className="p-2 bg-gray-100 rounded text-xs mb-2">
                            {task.contactName && (
                              <p className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                <span className="font-medium">{task.contactName}</span>
                                {task.contactEmail && <span className="text-gray-500">({task.contactEmail})</span>}
                              </p>
                            )}
                            {task.serviceType && (
                              <p className="flex items-center gap-1 mt-1">
                                <Briefcase className="h-3 w-3" />
                                <span>{task.serviceType}</span>
                              </p>
                            )}
                            {task.serviceContext && (
                              <p className="text-gray-500 mt-1 truncate" title={task.serviceContext}>{task.serviceContext}</p>
                            )}
                          </div>
                        )}
                        
                        <div className="flex items-center gap-2 mb-3">
                          <Badge className={getPriorityColor(task.priority)} variant="outline">{task.priority}</Badge>
                          {task.autoGenerated && <Badge variant="secondary" className="text-xs">Auto</Badge>}
                          {task.dueDate && (
                            <span className="text-xs text-gray-500 flex items-center">
                              <Clock className="h-3 w-3 mr-1" />
                              {formatShortDate(task.dueDate)}
                            </span>
                          )}
                        </div>
                        
                        {/* Action Buttons */}
                        <Button size="sm" className="w-full bg-blue-600 hover:bg-blue-700 text-white" onClick={() => transitionTaskStatus(task.id, 'in_progress')}>
                          <ArrowRight className="h-3 w-3 mr-1" />
                          Start Task
                        </Button>
                      </div>
                    ))}

                    {/* System Alerts */}
                    {stats.pending_contacts > 0 && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">Respond to {stats.pending_contacts} new contact(s)</p>
                            <Badge className="bg-red-100 text-red-800 mt-1 text-xs">urgent</Badge>
                          </div>
                        </div>
                      </div>
                    )}

                    {bookings.filter(b => b.status === 'pending').length > 0 && (
                      <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                        <div className="flex items-start gap-2">
                          <Calendar className="h-5 w-5 text-orange-500 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">Confirm {bookings.filter(b => b.status === 'pending').length} pending booking(s)</p>
                            <Badge className="bg-orange-100 text-orange-800 mt-1 text-xs">high</Badge>
                          </div>
                        </div>
                      </div>
                    )}

                    {tasks.filter(t => t.status === 'pending').length === 0 && stats.pending_contacts === 0 && bookings.filter(b => b.status === 'pending').length === 0 && (
                      <p className="text-sm text-gray-500 text-center py-4">No pending tasks</p>
                    )}
                  </CardContent>
                </Card>

                {/* In Progress */}
                <Card className="border-0 shadow-sm border-t-4 border-t-blue-500">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center justify-between">
                      <span className="flex items-center">
                        <div className="h-2 w-2 bg-blue-500 rounded-full mr-2"></div>
                        In Progress
                      </span>
                      <Badge variant="secondary">{tasks.filter(t => t.status === 'in_progress').length}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 max-h-[600px] overflow-y-auto">
                    {tasks.filter(t => t.status === 'in_progress').map(task => (
                      <div key={task.id} className="p-4 rounded-lg bg-white border border-blue-200 shadow-sm">
                        <div className="flex items-start justify-between mb-2">
                          <p className="text-sm font-semibold text-gray-900">{task.title}</p>
                          <button onClick={() => deleteTask(task.id)} className="text-gray-400 hover:text-red-500">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        {task.description && <p className="text-xs text-gray-600 mb-2">{task.description}</p>}
                        
                        {/* Contact & Service Context */}
                        {(task.contactName || task.serviceType) && (
                          <div className="p-2 bg-blue-50 rounded text-xs mb-2">
                            {task.contactName && (
                              <p className="flex items-center gap-1">
                                <User className="h-3 w-3" />
                                <span className="font-medium">{task.contactName}</span>
                              </p>
                            )}
                            {task.serviceType && (
                              <p className="flex items-center gap-1 mt-1">
                                <Briefcase className="h-3 w-3" />
                                <span>{task.serviceType}</span>
                              </p>
                            )}
                          </div>
                        )}
                        
                        <div className="flex items-center gap-2 mb-3">
                          <Badge className={getPriorityColor(task.priority)} variant="outline">{task.priority}</Badge>
                          {task.startedAt && (
                            <span className="text-xs text-blue-600 flex items-center">
                              <Clock className="h-3 w-3 mr-1" />
                              Started: {formatShortDate(task.startedAt)}
                            </span>
                          )}
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="flex-1" onClick={() => transitionTaskStatus(task.id, 'pending')}>
                            <ArrowLeft className="h-3 w-3 mr-1" />
                            Back
                          </Button>
                          <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700 text-white" onClick={() => transitionTaskStatus(task.id, 'completed')}>
                            <Check className="h-3 w-3 mr-1" />
                            Complete
                          </Button>
                        </div>
                      </div>
                    ))}
                    {tasks.filter(t => t.status === 'in_progress').length === 0 && (
                      <p className="text-sm text-gray-500 text-center py-8">No tasks in progress</p>
                    )}
                  </CardContent>
                </Card>

                {/* Completed */}
                <Card className="border-0 shadow-sm border-t-4 border-t-green-500">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center justify-between">
                      <span className="flex items-center">
                        <div className="h-2 w-2 bg-green-500 rounded-full mr-2"></div>
                        Completed
                      </span>
                      <Badge variant="secondary">{tasks.filter(t => t.status === 'completed').length}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 max-h-[600px] overflow-y-auto">
                    {tasks.filter(t => t.status === 'completed').map(task => (
                      <div key={task.id} className="p-4 rounded-lg bg-gray-50 border border-gray-200 opacity-75">
                        <div className="flex items-start justify-between mb-2">
                          <p className="text-sm font-medium text-gray-500 line-through">{task.title}</p>
                          <button onClick={() => deleteTask(task.id)} className="text-gray-400 hover:text-red-500">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        
                        {task.contactName && (
                          <p className="text-xs text-gray-400 mb-2">
                            <User className="h-3 w-3 inline mr-1" />
                            {task.contactName}
                          </p>
                        )}
                        
                        <div className="flex items-center justify-between">
                          {task.completedAt && (
                            <span className="text-xs text-green-600 flex items-center">
                              <CheckSquare className="h-3 w-3 mr-1" />
                              {formatShortDate(task.completedAt)}
                            </span>
                          )}
                          <Button size="sm" variant="ghost" className="text-xs" onClick={() => transitionTaskStatus(task.id, 'pending')}>
                            Reopen
                          </Button>
                        </div>
                      </div>
                    ))}
                    {tasks.filter(t => t.status === 'completed').length === 0 && (
                      <p className="text-sm text-gray-500 text-center py-8">No completed tasks</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {activeTab === 'blog' && (
            <BlogTab token={token} />
          )}
        </main>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-3 sm:items-center">
          <Card className="max-h-[92vh] w-full max-w-md overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>{editingPayment ? 'Edit Payment' : 'Log Payment'}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => { setShowPaymentModal(false); resetPaymentForm(); }}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedBooking && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium">{selectedBooking.name}</p>
                  <p className="text-sm text-gray-500">{selectedBooking.serviceType}</p>
                  <p className="text-sm text-gray-500">Total Cost: ${selectedBooking.totalCost || 'Not set'}</p>
                  <p className="mt-2 text-xs text-gray-500">
                    {selectedBooking.status === 'completed'
                      ? 'This completed job will generate a paid invoice as soon as this payment is saved.'
                      : 'Payments can still be logged before completion, but the paid invoice will generate after the booking status is marked completed.'}
                  </p>
                </div>
              )}
              <div>
                <Label>Amount ($)</Label>
                <Input
                  type="number"
                  placeholder="Enter amount"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                />
              </div>
              <div>
                <Label>Payment Type</Label>
                <Select value={paymentForm.paymentType} onValueChange={(v) => setPaymentForm({ ...paymentForm, paymentType: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="deposit">Deposit</SelectItem>
                    <SelectItem value="partial">Partial Payment</SelectItem>
                    <SelectItem value="full">Full Payment</SelectItem>
                    <SelectItem value="refund">Refund</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Payment Method</Label>
                <Select value={paymentForm.paymentMethod} onValueChange={(v) => setPaymentForm({ ...paymentForm, paymentMethod: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="check">Check</SelectItem>
                    <SelectItem value="zelle">Zelle</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <Label>Payment Date</Label>
                  <Input
                    type="date"
                    value={paymentForm.paymentDate}
                    onChange={(e) => setPaymentForm({ ...paymentForm, paymentDate: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Transaction ID (optional)</Label>
                  <Input
                    placeholder="Reference / check number"
                    value={paymentForm.transactionId}
                    onChange={(e) => setPaymentForm({ ...paymentForm, transactionId: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Invoice Line Items</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addPaymentLineItem}>
                    <Plus className="mr-2 h-3 w-3" />
                    Add Item
                  </Button>
                </div>
                {paymentForm.lineItems.map((item, index) => (
                  <div key={index} className="rounded-lg border border-gray-200 p-3">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-12">
                      <div className="sm:col-span-6">
                        <Label>Description</Label>
                        <Input
                          placeholder="Service description"
                          value={item.description}
                          onChange={(e) => updatePaymentLineItem(index, 'description', e.target.value)}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <Label>Qty</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.quantity}
                          onChange={(e) => updatePaymentLineItem(index, 'quantity', e.target.value)}
                        />
                      </div>
                      <div className="sm:col-span-3">
                        <Label>Unit Price</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unitPrice}
                          onChange={(e) => updatePaymentLineItem(index, 'unitPrice', e.target.value)}
                        />
                      </div>
                      <div className="sm:col-span-1 flex items-end">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={paymentForm.lineItems.length === 1}
                          onClick={() => removePaymentLineItem(index)}
                        >
                          <Trash2 className="h-4 w-4 text-gray-400 hover:text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div>
                <Label>Notes (optional)</Label>
                <Textarea
                  placeholder="Add notes..."
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                />
              </div>
              <Button className="w-full bg-green-600 hover:bg-green-700" onClick={savePayment}>
                {editingPayment ? 'Save Payment Changes' : 'Log Payment'}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {showPaymentHistoryModal && selectedBooking && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-3 sm:items-center">
          <Card className="max-h-[92vh] w-full max-w-2xl overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>{correctionTarget === 'paid' ? 'Edit Paid Amount' : correctionTarget === 'balance' ? 'Adjust Balance Inputs' : 'Adjust Net Profit Inputs'}</CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  {getCorrectionHelpText()}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowPaymentHistoryModal(false)}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg bg-gray-50 p-3">
                <p className="font-medium">{selectedBooking.name}</p>
                <p className="text-sm text-gray-500">{selectedBooking.serviceType}</p>
                <p className="text-sm text-gray-500">{selectedBooking.currentAddress}</p>
                <div className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                  <div>
                    <span className="text-gray-500">Total Cost:</span>
                    <p className="font-semibold">${getBookingPaymentStatus(selectedBooking).totalCost.toLocaleString()}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Paid:</span>
                    <p className="font-semibold text-green-600">${getBookingPaymentStatus(selectedBooking).totalPaid.toLocaleString()}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Balance:</span>
                    <p className="font-semibold text-orange-600">${getBookingPaymentStatus(selectedBooking).balance.toLocaleString()}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Net Profit:</span>
                    <p className={`font-semibold ${getBookingPaymentStatus(selectedBooking).netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      ${getBookingPaymentStatus(selectedBooking).netProfit.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                {(correctionTarget === 'balance' || correctionTarget === 'netProfit') && (
                  <Button
                    variant="outline"
                    className="mr-2"
                    onClick={() => {
                      setShowPaymentHistoryModal(false);
                      openFinancialsModal(selectedBooking, correctionTarget === 'balance' ? 'totalCost' : 'contractorCost');
                    }}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    {correctionTarget === 'balance' ? 'Edit Total Cost' : 'Edit Contractor Cost'}
                  </Button>
                )}
                <Button className="bg-green-600 hover:bg-green-700" onClick={() => { setShowPaymentHistoryModal(false); openPaymentModalForBooking(selectedBooking); }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Payment
                </Button>
              </div>

              {getBookingPayments(selectedBooking.id).length === 0 ? (
                <p className="py-8 text-center text-gray-500">No payments recorded for this booking yet.</p>
              ) : (
                <div className="space-y-3">
                  {getBookingPayments(selectedBooking.id).map((payment) => (
                    <div key={payment.id} className="rounded-lg border border-gray-200 p-3">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="font-medium text-gray-900">
                            {payment.paymentType} payment • {payment.paymentMethod}
                          </p>
                          <p className="text-xs text-gray-500">
                            Paid on {formatShortDate(payment.paymentDate || payment.createdAt, { timezoneLessIsUtc: true })}
                            {payment.transactionId ? ` • Ref: ${payment.transactionId}` : ''}
                          </p>
                        </div>
                        <p className={`text-lg font-bold ${payment.paymentType === 'refund' ? 'text-red-600' : 'text-green-600'}`}>
                          {payment.paymentType === 'refund' ? '-' : '+'}${payment.amount.toLocaleString()}
                        </p>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={() => openPaymentModalForEdit(payment, selectedBooking)}>
                          <Edit className="h-3 w-3 mr-1" />
                          Edit Amount
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => deletePayment(payment.id)}>
                          <Trash2 className="h-3 w-3 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Task Modal */}
      {showTaskModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-3 sm:items-center">
          <Card className="max-h-[92vh] w-full max-w-md overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Create Task</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowTaskModal(false)}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedBooking && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Linked to:</p>
                  <p className="font-medium">{selectedBooking.name} - {selectedBooking.serviceType}</p>
                </div>
              )}
              <div>
                <Label>Task Title</Label>
                <Input
                  placeholder="Enter task title"
                  value={taskForm.title}
                  onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                />
              </div>
              <div>
                <Label>Description (optional)</Label>
                <Input
                  placeholder="Add description..."
                  value={taskForm.description}
                  onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                />
              </div>
              <div>
                <Label>Task Type</Label>
                <Select value={taskForm.taskType} onValueChange={(v) => setTaskForm({ ...taskForm, taskType: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="follow_up">Follow Up</SelectItem>
                    <SelectItem value="confirmation">Confirmation</SelectItem>
                    <SelectItem value="quote_review">Quote Review</SelectItem>
                    <SelectItem value="schedule_call">Schedule Call</SelectItem>
                    <SelectItem value="send_invoice">Send Invoice</SelectItem>
                    <SelectItem value="collect_payment">Collect Payment</SelectItem>
                    <SelectItem value="pre_move_check">Pre-Move Check</SelectItem>
                    <SelectItem value="post_service_followup">Post-Service Followup</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={taskForm.priority} onValueChange={(v) => setTaskForm({ ...taskForm, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Due Date (optional)</Label>
                <Input
                  type="date"
                  value={taskForm.dueDate}
                  onChange={(e) => setTaskForm({ ...taskForm, dueDate: e.target.value })}
                />
              </div>
              <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={createTask}>
                Create Task
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Cost Modal */}
      {/* Cost Modal - Updated with contractor cost and labor hours */}
      {showCostModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-3 sm:items-center">
          <Card className="max-h-[92vh] w-full max-w-md overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>
                  {correctionTarget === 'contractorCost'
                    ? 'Edit Contractor Cost'
                    : correctionTarget === 'laborHours'
                    ? 'Edit Labor Hours'
                    : correctionTarget === 'netProfit'
                    ? 'Adjust Net Profit Inputs'
                    : 'Set Booking Financials'}
                </CardTitle>
                <p className="mt-1 text-sm text-gray-500">{getCorrectionHelpText()}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowCostModal(false)}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedBooking && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium">{selectedBooking.name}</p>
                  <p className="text-sm text-gray-500">{selectedBooking.serviceType}</p>
                  <p className="text-sm text-gray-500">{selectedBooking.currentAddress}</p>
                </div>
              )}
              <div>
                <Label>Total Cost ($) - What customer pays</Label>
                <Input
                  autoFocus={correctionTarget === 'totalCost' || correctionTarget === 'balance'}
                  type="number"
                  placeholder="Enter total cost for this job"
                  value={costForm.totalCost}
                  onChange={(e) => setCostForm({ ...costForm, totalCost: e.target.value })}
                />
              </div>
              <div>
                <Label>Contractor Cost ($) - Your expense</Label>
                <Input
                  autoFocus={correctionTarget === 'contractorCost' || correctionTarget === 'netProfit'}
                  type="number"
                  placeholder="Cost paid to contractors"
                  value={costForm.contractorCost}
                  onChange={(e) => setCostForm({ ...costForm, contractorCost: e.target.value })}
                />
                <p className="text-xs text-gray-500 mt-1">Deducted from revenue to calculate net profit</p>
              </div>
              <div>
                <Label>Labor Hours</Label>
                <Input
                  autoFocus={correctionTarget === 'laborHours'}
                  type="number"
                  step="0.5"
                  placeholder="Total hours for this job"
                  value={costForm.laborHours}
                  onChange={(e) => setCostForm({ ...costForm, laborHours: e.target.value })}
                />
              </div>
              <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={updateBookingFinancials}>
                Save Financials
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Convert Quote to Booking Modal */}
      {showConvertModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-3 sm:items-center">
          <Card className="max-h-[92vh] w-full max-w-md overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Convert to Booking</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowConvertModal(false)}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedQuote && (
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="font-medium">{selectedQuote.name}</p>
                  <p className="text-sm text-gray-500">{selectedQuote.email}</p>
                  <p className="text-sm text-gray-500">{selectedQuote.phone}</p>
                  {selectedQuote.message && (
                    <p className="text-xs text-gray-400 mt-2 truncate">{selectedQuote.message.substring(0, 100)}...</p>
                  )}
                </div>
              )}
              <div>
                <Label>Customer Name (if different)</Label>
                <Input
                  placeholder="Leave blank to use chatbot name"
                  value={convertForm.customerName || ''}
                  onChange={(e) => setConvertForm({ ...convertForm, customerName: e.target.value })}
                />
                <p className="text-xs text-gray-500 mt-1">Override if the chatbot captured the wrong name</p>
              </div>
              <div>
                <Label>Service Type</Label>
                <Select value={convertForm.serviceType} onValueChange={(v) => setConvertForm({ ...convertForm, serviceType: v })}>
                  <SelectTrigger><SelectValue placeholder="Select service type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Residential Moving">Residential Moving</SelectItem>
                    <SelectItem value="Commercial Moving">Commercial Moving</SelectItem>
                    <SelectItem value="Moving Support">Moving Support</SelectItem>
                    <SelectItem value="Residential Cleaning">Residential Cleaning</SelectItem>
                    <SelectItem value="Commercial Cleaning">Commercial Cleaning</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Preferred Date</Label>
                <Input
                  type="date"
                  value={convertForm.preferredDate}
                  onChange={(e) => setConvertForm({ ...convertForm, preferredDate: e.target.value })}
                />
              </div>
              <div>
                <Label>Current Address</Label>
                <Input
                  placeholder="Service address"
                  value={convertForm.currentAddress}
                  onChange={(e) => setConvertForm({ ...convertForm, currentAddress: e.target.value })}
                />
              </div>
              <div>
                <Label>Destination Address (if moving)</Label>
                <Input
                  placeholder="New address (optional)"
                  value={convertForm.newAddress}
                  onChange={(e) => setConvertForm({ ...convertForm, newAddress: e.target.value })}
                />
              </div>
              <Button className="w-full bg-green-600 hover:bg-green-700" onClick={convertQuoteToBooking}>
                Convert to Booking
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Reset Database Modal (Danger Zone) */}
      {showResetModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-3 sm:items-center">
          <Card className="max-h-[92vh] w-full max-w-lg overflow-y-auto border-2 border-red-200">
            <CardHeader className="bg-red-50 border-b border-red-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-red-100 p-2 rounded-full">
                    <AlertCircle className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <CardTitle className="text-red-700">Reset Database</CardTitle>
                    <p className="text-sm text-red-600 mt-1">This action cannot be undone</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setShowResetModal(false); setResetConfirmation(''); }}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4 pt-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-700 mb-3">The following records will be permanently deleted:</p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="flex items-center justify-between p-2 bg-white rounded border">
                    <span className="text-sm text-gray-600">Contacts</span>
                    <Badge variant="secondary">{dbStats.contacts}</Badge>
                  </div>
                  <div className="flex items-center justify-between p-2 bg-white rounded border">
                    <span className="text-sm text-gray-600">Bookings</span>
                    <Badge variant="secondary">{dbStats.bookings}</Badge>
                  </div>
                  <div className="flex items-center justify-between p-2 bg-white rounded border">
                    <span className="text-sm text-gray-600">Payments</span>
                    <Badge variant="secondary">{dbStats.payments}</Badge>
                  </div>
                  <div className="flex items-center justify-between p-2 bg-white rounded border">
                    <span className="text-sm text-gray-600">Tasks</span>
                    <Badge variant="secondary">{dbStats.tasks}</Badge>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-200 flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Total records to delete:</span>
                  <Badge className="bg-red-100 text-red-800">{dbStats.contacts + dbStats.bookings + dbStats.payments + dbStats.tasks}</Badge>
                </div>
              </div>

              <div className="border-2 border-red-200 rounded-lg p-4 bg-red-50">
                <Label className="text-red-700 font-medium">Type DELETE ALL DATA to confirm</Label>
                <Input
                  className="mt-2 border-red-300 focus:border-red-500 focus:ring-red-500"
                  placeholder="DELETE ALL DATA"
                  value={resetConfirmation}
                  onChange={(e) => setResetConfirmation(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-1 gap-3 pt-2 sm:flex">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => { setShowResetModal(false); setResetConfirmation(''); }}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  onClick={handleResetDatabase}
                  disabled={resetConfirmation !== 'DELETE ALL DATA' || isResetting}
                >
                  {isResetting ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete All Records
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 px-6 pb-24 pt-4 text-center text-sm text-gray-500 lg:pb-4">
        <p>© {new Date().getFullYear()} Swift Move and Clean. Admin Dashboard v2.0</p>
      </footer>
    </div>
  );
};
